import {
  GeofenceSiteGroup,
  useCreateGeofenceSiteGroupMutation,
  useDeleteGeofenceSiteGroupMutation,
  useUpdateGeofenceSiteGroupMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseGeofenceSiteGroupMutationProps = {}

export type UseGeofenceSiteGroupMutationResult = {
  createGeofenceSiteGroup: (geofenceSiteGroup: Partial<GeofenceSiteGroup>, callback: EntityMutationCallback<GeofenceSiteGroup> | undefined) => void
  updateGeofenceSiteGroup: (geofenceSiteGroup: Partial<GeofenceSiteGroup>, callback: EntityMutationCallback<GeofenceSiteGroup> | undefined) => void
  deleteGeofenceSiteGroup: (geofenceSiteGroupId: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useGeofenceSiteGroupMutation = (props: UseGeofenceSiteGroupMutationProps = {}): UseGeofenceSiteGroupMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createGeofenceSiteGroup, {isLoading: isCreating}] = useCreateGeofenceSiteGroupMutation()
  const [_updateGeofenceSiteGroup, {isLoading: isUpdating}] = useUpdateGeofenceSiteGroupMutation()
  const [_deleteGeofenceSiteGroup, {isLoading: isDeleting}] = useDeleteGeofenceSiteGroupMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useGeofenceSiteGroupUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.geofencesitegroup", "Geofence Site Group"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useGeofenceSiteGroupUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useGeofenceSiteGroupUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.geofencesitegroup", "Geofence Site Group"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useGeofenceSiteGroupUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useGeofenceSiteGroupUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.geofencesitegroup", "Geofence Site Group"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useGeofenceSiteGroupUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<GeofenceSiteGroup>, callback: EntityMutationCallback<GeofenceSiteGroup> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.geofencesitegroup", "Geofence Site Group") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback<GeofenceSiteGroup> | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.geofencesitegroup", "Geofence Site Group") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.geofencesitegroup", "Geofence Site Group") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((geofenceSiteGroupId: string, callback: EntityMutationCallback<GeofenceSiteGroup> | undefined) => {
    const promise = _deleteGeofenceSiteGroup({id: geofenceSiteGroupId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteGeofenceSiteGroup, handleDeleteResult])

  const createGeofenceSiteGroup = useCallback((geofenceSiteGroup: Partial<GeofenceSiteGroup>, callback: EntityMutationCallback<GeofenceSiteGroup> | undefined) => {
    const promise = _createGeofenceSiteGroup(geofenceSiteGroup).unwrap()
    handleCreateResult(promise, callback)
  }, [_createGeofenceSiteGroup, handleCreateResult])

  const updateGeofenceSiteGroup = useCallback((geofenceSiteGroup: Partial<GeofenceSiteGroup>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateGeofenceSiteGroup(geofenceSiteGroup).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateGeofenceSiteGroup, handleUpdateResult])

  const deleteGeofenceSiteGroup = useCallback((geofenceSiteGroupId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.geofencesitegroup", "Geofence Site Group"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.geofencesitegroup.confirm", "Are you sure you want to delete this geofence site group?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(geofenceSiteGroupId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createGeofenceSiteGroup: createGeofenceSiteGroup,
    updateGeofenceSiteGroup: updateGeofenceSiteGroup,
    deleteGeofenceSiteGroup: deleteGeofenceSiteGroup,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
